import { useEffect, useState, useRef } from "react";
import { ArrowLeft, Send, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";

// Sample conversation data
const conversationData = [
  {
    id: 1,
    sender: "ContentBot",
    avatarSrc: "/crew-agents/content-generator.jpg",
    message: "Hi there! I've been analyzing your blog content performance over the last quarter.",
    timestamp: "2 days ago",
    isUser: false
  },
  {
    id: 2,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "Great! What have you found so far?",
    timestamp: "2 days ago",
    isUser: true
  },
  {
    id: 3,
    sender: "ContentBot",
    avatarSrc: "/crew-agents/content-generator.jpg",
    message: "I've identified three main insights:\n\n1. Posts about AI tools are getting 3x more engagement than other topics\n2. Long-form content (>2000 words) has a 45% higher conversion rate\n3. Posts published on Tuesdays and Thursdays get 27% more social shares",
    timestamp: "2 days ago",
    isUser: false
  },
  {
    id: 4,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "That's really valuable information. Could you analyze our competitor's content strategy too?",
    timestamp: "2 days ago",
    isUser: true
  },
  {
    id: 5,
    sender: "ContentBot",
    avatarSrc: "/crew-agents/content-generator.jpg",
    message: "I'll coordinate with CompetitorBot to analyze their content strategy. We'll provide a comparative analysis within 24 hours.",
    timestamp: "2 days ago",
    isUser: false
  },
  {
    id: 6,
    sender: "ContentBot",
    avatarSrc: "/crew-agents/content-generator.jpg",
    message: "The competitor analysis is complete. Would you like to see the full report now?",
    timestamp: "1 day ago",
    isUser: false
  },
  {
    id: 7,
    sender: "ContentBot",
    avatarSrc: "/crew-agents/content-generator.jpg",
    message: "I've also prepared some content recommendations based on the insights we gathered. These should help increase engagement and conversion rates.",
    timestamp: "10 minutes ago",
    isUser: false
  }
];

const ChatMessage = ({ message }: { message: any }) => {
  const isUser = message.isUser;
  
  return (
    <div className={`flex gap-3 mb-4 ${isUser ? 'justify-end' : ''}`}>
      {!isUser && (
        <img 
          src={message.avatarSrc} 
          alt={message.sender}
          className="h-8 w-8 rounded-full object-cover mt-1"
        />
      )}
      
      <div className={`max-w-[70%] ${isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'} rounded-lg px-4 py-2`}>
        <div className="flex justify-between items-baseline mb-1">
          <span className={`font-medium text-sm ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
            {message.sender}
          </span>
          <span className={`text-xs ml-4 ${isUser ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
            {message.timestamp}
          </span>
        </div>
        <p className={`text-sm whitespace-pre-line ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
          {message.message}
        </p>
      </div>
      
      {isUser && (
        <img 
          src={message.avatarSrc} 
          alt={message.sender}
          className="h-8 w-8 rounded-full object-cover mt-1"
        />
      )}
    </div>
  );
};

const DirectMessagePage = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [conversation, setConversation] = useState(conversationData);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    document.title = "Chat with ContentBot | PageMind Admin";
    
    // Scroll to bottom of messages
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (message.trim()) {
      // Add user message
      const newUserMessage = {
        id: conversation.length + 1,
        sender: "You",
        avatarSrc: "/crew-agents/page-semantic.png",
        message,
        timestamp: "Just now",
        isUser: true
      };
      
      setConversation([...conversation, newUserMessage]);
      setMessage("");
      
      // Simulate response after a delay (in a real app, this would be an API call)
      setTimeout(() => {
        const botResponse = {
          id: conversation.length + 2,
          sender: "ContentBot",
          avatarSrc: "/crew-agents/content-generator.jpg",
          message: "I'll analyze this request and get back to you shortly with detailed recommendations.",
          timestamp: "Just now",
          isUser: false
        };
        
        setConversation(prev => [...prev, botResponse]);
      }, 1000);
    }
  };

  return (
    <div className="container mx-auto p-6 h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 pb-4 border-b">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/insights/messages')}
            className="mr-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center gap-3">
            <img 
              src="/crew-agents/content-generator.jpg" 
              alt="ContentBot"
              className="h-10 w-10 rounded-full object-cover"
            />
            <div>
              <div className="font-bold flex items-center gap-2">
                ContentBot
                <Badge variant="outline" className="text-xs">Content Analyzer</Badge>
                <Badge className="bg-green-500">Online</Badge>
              </div>
              <div className="text-xs text-muted-foreground">Last active: Just now</div>
            </div>
          </div>
        </div>
        
        <div>
          <Button variant="outline" size="sm">
            View Profile
          </Button>
        </div>
      </div>
      
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-4 py-4 bg-background/80 rounded-lg">
        {conversation.map(msg => (
          <ChatMessage key={msg.id} message={msg} />
        ))}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Message Input */}
      <div className="mt-4">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="rounded-full"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          
          <Input
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1"
          />
          
          <Button
            type="submit"
            disabled={!message.trim()}
          >
            <Send className="h-4 w-4 mr-2" />
            Send
          </Button>
        </form>
      </div>
    </div>
  );
};

export default DirectMessagePage;